import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { getApiBase } from '../utils/api';
import { Search, X } from 'lucide-react';

/**
 * OrganizationSelector component for multi-org SSO login.
 * Displayed when a user has access to multiple organizations.
 */
const OrganizationSelector = ({
  eligibleOrganizations,
  selectionToken,
  email,
  onSuccess,
  onError,
  onCancel
}) => {
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter organizations based on search
  const filteredOrganizations = useMemo(() => {
    if (!searchTerm) return eligibleOrganizations;
    const searchLower = searchTerm.toLowerCase();
    return eligibleOrganizations.filter(org =>
      org.organization_name?.toLowerCase().includes(searchLower)
    );
  }, [eligibleOrganizations, searchTerm]);

  // Show search if more than 5 organizations
  const showSearch = eligibleOrganizations.length > 5;

  const handleSelectOrganization = async () => {
    if (!selectedOrgId) {
      onError('Please select an organization');
      return;
    }

    setLoading(true);
    try {
      const apiBase = getApiBase();
      const response = await axios.post(
        `${apiBase}/auth/sso/select-organization`,
        {
          organization_id: selectedOrgId
        },
        { withCredentials: true }
      );

      onSuccess(response.data);
    } catch (err) {
      console.error('Organization selection failed:', err);
      onError(err.response?.data?.detail || 'Failed to complete login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Select Organization
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {email}
          </p>
        </div>

        {/* Organization List */}
        <div className="px-6 py-4 max-h-96 overflow-hidden flex flex-col">
          <p className="text-sm text-gray-600 mb-4">
            You have access to multiple organizations. Please select which one you'd like to sign in to:
          </p>

          {/* Search Input - only shown when 5+ organizations */}
          {showSearch && (
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search organizations..."
                className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          {/* Results count */}
          {showSearch && (
            <div className="text-xs text-gray-500 mb-2">
              {filteredOrganizations.length} of {eligibleOrganizations.length} organizations
              {searchTerm && ` matching "${searchTerm}"`}
            </div>
          )}

          <div className="space-y-2 overflow-y-auto flex-1">
            {filteredOrganizations.length === 0 ? (
              <div className="text-center py-4 text-gray-500 text-sm">
                No organizations match your search
              </div>
            ) : filteredOrganizations.map((org) => (
              <label
                key={org.organization_id}
                className={`
                  flex items-center p-4 border rounded-lg cursor-pointer transition-colors
                  ${selectedOrgId === org.organization_id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                <input
                  type="radio"
                  name="organization"
                  value={org.organization_id}
                  checked={selectedOrgId === org.organization_id}
                  onChange={() => setSelectedOrgId(org.organization_id)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {org.organization_name}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {org.user_exists && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Existing member
                      </span>
                    )}
                    {org.has_invite && !org.user_exists && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Pending invite
                      </span>
                    )}
                    {org.is_corporate_domain && !org.user_exists && !org.has_invite && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        Corporate domain
                      </span>
                    )}
                    {org.role && (
                      <span className="text-xs text-gray-500 capitalize">
                        {org.role}
                      </span>
                    )}
                  </div>
                </div>
                <svg
                  className={`h-5 w-5 ${selectedOrgId === org.organization_id ? 'text-indigo-600' : 'text-gray-300'}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </label>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSelectOrganization}
            disabled={loading || !selectedOrgId}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSelector;
